import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { socialService, SocialPost } from '../../services/socialService';
import { useAuth } from '../../contexts/AuthContext';
import { aiCharacterService, AICharacter } from '../../services/aiCharacterService';
import { extractMentionedCharacterIds } from '../../utils/mentionUtils';
import PageHeader from '../common/PageHeader';
import PostCard from './PostCard';
import './Social.css';

interface SocialProps {
  onClose: () => void;
  onToggleSidebar: () => void;
}

const Social: React.FC<SocialProps> = ({ onClose, onToggleSidebar }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [posts, setPosts] = useState<SocialPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newPostContent, setNewPostContent] = useState('');
  const [posting, setPosting] = useState(false);
  const [editingPostId, setEditingPostId] = useState<number | null>(null);
  const [editContent, setEditContent] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  
  // Mention dropdown state
  const [aiCharacters, setAiCharacters] = useState<AICharacter[]>([]);
  const [showMentionDropdown, setShowMentionDropdown] = useState(false);
  const [mentionPosition, setMentionPosition] = useState({ top: 0, left: 0 });
  const [mentionSearchTerm, setMentionSearchTerm] = useState('');
  const [mentionStartIndex, setMentionStartIndex] = useState(-1);
  const mentionDropdownRef = useRef<HTMLDivElement>(null);
  
  // Track posts with pending AI responses
  const [postsWithPendingAI, setPostsWithPendingAI] = useState<Set<number>>(new Set());
  const pollingIntervalsRef = useRef<Map<number, NodeJS.Timeout>>(new Map());

  useEffect(() => {
    loadPosts();
    loadAICharacters();
  }, [user?.id]);

  // Cleanup polling intervals on unmount
  useEffect(() => {
    return () => {
      pollingIntervalsRef.current.forEach(interval => clearInterval(interval));
      pollingIntervalsRef.current.clear();
    };
  }, []);

  useEffect(() => {
    // Close mention dropdown when clicking outside
    const handleClickOutside = (event: MouseEvent) => {
      if (
        mentionDropdownRef.current &&
        !mentionDropdownRef.current.contains(event.target as Node) &&
        textareaRef.current &&
        !textareaRef.current.contains(event.target as Node)
      ) {
        setShowMentionDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [newPostContent]);

  const loadAICharacters = async () => {
    if (!user?.id) return;
    
    try {
      const response = await aiCharacterService.getUserCharacters({
        user_id: user.id.toString(),
        is_active: true,
        limit: 50
      });
      
      if (response.success && response.characters) {
        setAiCharacters(response.characters);
      }
    } catch (error) {
      console.error('Failed to load AI characters:', error);
    }
  };

  const loadPosts = async () => {
    try {
      setLoading(true);
      setError(null);
      const fetchedPosts = await socialService.getPosts(50, 0);
      setPosts(fetchedPosts);
    } catch (error) {
      console.error('Failed to load posts:', error);
      setError('Failed to load posts. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePost = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newPostContent) return;

    try {
      setPosting(true);
      setError(null);
      const newPost = await socialService.createPost({ content: newPostContent });
      setPosts([newPost, ...posts]);
      
      // Check if post has mentions
      const mentionedCharacterIds = extractMentionedCharacterIds(newPostContent);
      if (mentionedCharacterIds.length > 0) {
        // Mark post as pending AI responses
        setPostsWithPendingAI(prev => new Set(prev).add(newPost.id));
        
        // Start polling for comments
        startPollingForComments(newPost.id, newPost.comments_count || 0);
      }
      
      setNewPostContent('');
    } catch (error) {
      console.error('Failed to create post:', error);
      setError('Failed to create post. Please try again.');
    } finally {
      setPosting(false);
    }
  };

  const startPollingForComments = (postId: number, initialCommentCount: number) => {
    // Clear any existing interval for this post
    const existingInterval = pollingIntervalsRef.current.get(postId);
    if (existingInterval) {
      clearInterval(existingInterval);
    }

    let pollCount = 0;
    const maxPolls = 30; // Poll for up to 30 times (5 minutes if polling every 10 seconds)
    const pollInterval = 10000; // Poll every 10 seconds

    const interval = setInterval(async () => {
      pollCount++;
      
      try {
        const comments = await socialService.getComments(postId);
        const currentCommentCount = comments.length;
        
        // If we got new comments, update the post and stop polling
        if (currentCommentCount > initialCommentCount) {
          // Update the post's comment count
          setPosts(prevPosts => 
            prevPosts.map(post => 
              post.id === postId 
                ? { ...post, comments_count: currentCommentCount }
                : post
            )
          );
          
          // Remove from pending set
          setPostsWithPendingAI(prev => {
            const newSet = new Set(prev);
            newSet.delete(postId);
            return newSet;
          });
          
          // Stop polling
          clearInterval(interval);
          pollingIntervalsRef.current.delete(postId);
        } else if (pollCount >= maxPolls) {
          // Stop polling after max attempts
          setPostsWithPendingAI(prev => {
            const newSet = new Set(prev);
            newSet.delete(postId);
            return newSet;
          });
          clearInterval(interval);
          pollingIntervalsRef.current.delete(postId);
        }
      } catch (error) {
        console.error(`Error polling comments for post ${postId}:`, error);
        // Stop polling on error
        setPostsWithPendingAI(prev => {
          const newSet = new Set(prev);
          newSet.delete(postId);
          return newSet;
        });
        clearInterval(interval);
        pollingIntervalsRef.current.delete(postId);
      }
    }, pollInterval);

    pollingIntervalsRef.current.set(postId, interval);
  };

  const handleDeletePost = async (postId: number) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;

    try {
      await socialService.deletePost(postId);
      setPosts(posts.filter(post => post.id !== postId));
    } catch (error) {
      console.error('Failed to delete post:', error);
      setError('Failed to delete post. Please try again.');
    }
  };

  const handleStartEdit = (post: SocialPost) => {
    setEditingPostId(post.id);
    setEditContent(post.content);
  };

  const handleCancelEdit = () => {
    setEditingPostId(null);
    setEditContent('');
  };

  const handleUpdatePost = async (postId: number) => {
    if (!editContent) return;

    try {
      const updatedPost = await socialService.updatePost(postId, { content: editContent });
      setPosts(posts.map(post => post.id === postId ? updatedPost : post));
      setEditingPostId(null);
      setEditContent('');
    } catch (error) {
      console.error('Failed to update post:', error);
      setError('Failed to update post. Please try again.');
    }
  };

  const handleLikeToggle = async (post: SocialPost) => {
    try {
      if (post.is_liked) {
        await socialService.unlikePost(post.id);
        setPosts(posts.map(p => 
          p.id === post.id 
            ? { ...p, is_liked: false, likes_count: (p.likes_count || 1) - 1 }
            : p
        ));
      } else {
        await socialService.likePost(post.id);
        setPosts(posts.map(p => 
          p.id === post.id 
            ? { ...p, is_liked: true, likes_count: (p.likes_count || 0) + 1 }
            : p
        ));
      }
    } catch (error) {
      console.error('Failed to toggle like:', error);
      setError('Failed to update like. Please try again.');
    }
  };

  const getCharacterCount = (text: string): number => {
    return text.length;
  };

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    const cursorPosition = e.target.selectionStart;
    
    setNewPostContent(value);
    
    // Check for @ mention trigger
    const textBeforeCursor = value.substring(0, cursorPosition);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');
    
    if (lastAtIndex !== -1) {
      // Check if there's a space or newline after @ (meaning mention is complete)
      const textAfterAt = textBeforeCursor.substring(lastAtIndex + 1);
      const hasSpaceOrNewline = /[\s\n]/.test(textAfterAt);
      
      // Check if we're already inside a mention syntax
      const isInsideMention = /@\[character:\d+\]\([^)]*$/.test(textBeforeCursor);
      
      if (!hasSpaceOrNewline && !isInsideMention) {
        // Show mention dropdown
        const searchTerm = textAfterAt.toLowerCase();
        setMentionSearchTerm(searchTerm);
        setMentionStartIndex(lastAtIndex);
        
        // Calculate dropdown position
        if (textareaRef.current) {
          const textarea = textareaRef.current;
          const rect = textarea.getBoundingClientRect();
          
          // Estimate position based on cursor (simplified)
          const textBeforeCursorLines = textBeforeCursor.split('\n').length - 1;
          const lineHeight = 24; // Approximate line height
          const containerRect = textarea.parentElement?.getBoundingClientRect();
          
          if (containerRect) {
            setMentionPosition({
              top: (textBeforeCursorLines * lineHeight) + lineHeight + 5,
              left: 10
            });
          }
        }
        
        setShowMentionDropdown(true);
      } else {
        setShowMentionDropdown(false);
      }
    } else {
      setShowMentionDropdown(false);
    }
  };

  const handleCharacterSelect = (character: AICharacter) => {
    if (mentionStartIndex === -1 || !textareaRef.current) return;
    
    const currentValue = textareaRef.current.value;
    const cursorPos = textareaRef.current.selectionStart;
    const textBefore = currentValue.substring(0, mentionStartIndex);
    const textAfter = currentValue.substring(cursorPos);
    const mentionSyntax = `@[character:${character.id}](${character.name})`;
    
    const newContent = textBefore + mentionSyntax + ' ' + textAfter;
    setNewPostContent(newContent);
    setShowMentionDropdown(false);
    setMentionStartIndex(-1);
    setMentionSearchTerm('');
    
    // Set cursor position after the mention
    setTimeout(() => {
      if (textareaRef.current) {
        const newCursorPos = textBefore.length + mentionSyntax.length + 1;
        textareaRef.current.setSelectionRange(newCursorPos, newCursorPos);
        textareaRef.current.focus();
      }
    }, 0);
  };

  const getFilteredCharacters = (): AICharacter[] => {
    if (!mentionSearchTerm) {
      return aiCharacters.slice(0, 10); // Limit to 10 characters
    }
    
    return aiCharacters
      .filter(char => 
        char.name.toLowerCase().includes(mentionSearchTerm)
      )
      .slice(0, 10);
  };

  // Navigate to post detail page
  const handleNavigateToPost = (postId: number) => {
    navigate(`/social/${postId}`);
  };

  return (
    <div className="social-page">
      <PageHeader
        onToggleSidebar={onToggleSidebar}
        centerContent={
          <h1 className="social-title">Social</h1>
        }
      />

      <div className="social-container">
        {/* Create Post Section */}
        <div className="social-create-post">
          <form onSubmit={handleCreatePost}>
            <div className="social-post-input-container" style={{ position: 'relative' }}>
              <textarea
                ref={textareaRef}
                value={newPostContent}
                onChange={handleTextareaChange}
                onKeyDown={(e) => {
                  if (showMentionDropdown && e.key === 'ArrowDown') {
                    e.preventDefault();
                    // Could add keyboard navigation here
                  } else if (showMentionDropdown && e.key === 'Escape') {
                    setShowMentionDropdown(false);
                  }
                }}
                placeholder="What's on your mind?"
                className="social-post-input"
                disabled={posting}
                rows={1}
              />
              
              {/* Mention Dropdown */}
              {showMentionDropdown && (
                <div
                  ref={mentionDropdownRef}
                  className="social-mention-dropdown"
                  style={{
                    position: 'absolute',
                    top: `${mentionPosition.top}px`,
                    left: `${mentionPosition.left}px`,
                    zIndex: 1000,
                    marginTop: '0.5rem'
                  }}
                >
                  {getFilteredCharacters().length > 0 ? (
                    getFilteredCharacters().map((character) => (
                      <div
                        key={character.id}
                        className="social-mention-item"
                        onClick={() => handleCharacterSelect(character)}
                        onMouseDown={(e) => e.preventDefault()} // Prevent textarea blur
                      >
                        <span className="social-mention-name">{character.name}</span>
                        <span className="social-mention-provider">{character.model_provider}</span>
                      </div>
                    ))
                  ) : (
                    <div className="social-mention-no-results">No characters found</div>
                  )}
                </div>
              )}
            </div>
            <div className="social-post-actions">
              <span className="social-char-count">
                {getCharacterCount(newPostContent)}
              </span>
              <button 
                type="submit" 
                disabled={posting || !newPostContent}
                className="social-post-btn"
              >
                {posting ? 'Posting...' : 'Post'}
              </button>
            </div>
          </form>
        </div>

        {/* Error Message */}
        {error && (
          <div className="social-error-message">
            <span>{error}</span>
            <button 
              className="social-error-dismiss"
              onClick={() => setError(null)}
            >
              ×
            </button>
          </div>
        )}

        {/* Posts Feed */}
        <div className="social-feed">
          {loading ? (
            <div className="social-loading-container">
              <div className="social-loading-spinner"></div>
              <p>Loading posts...</p>
            </div>
          ) : posts.length === 0 ? (
            <div className="social-no-posts">
              <p>No posts yet. Be the first to share something!</p>
            </div>
          ) : (
            posts.map((post) => (
              <PostCard
                key={post.id}
                post={post}
                currentUserId={user?.id}
                isEditing={editingPostId === post.id}
                editContent={editContent}
                onEditContentChange={setEditContent}
                onStartEdit={() => handleStartEdit(post)}
                onSaveEdit={() => handleUpdatePost(post.id)}
                onCancelEdit={handleCancelEdit}
                onDelete={() => handleDeletePost(post.id)}
                onLikeToggle={() => handleLikeToggle(post)}
                onCommentClick={() => handleNavigateToPost(post.id)}
                isWaitingForAI={postsWithPendingAI.has(post.id)}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default Social;

